import React, {useEffect, useState} from 'react';
import {Card, Col, Form, Row} from 'react-bootstrap';
import {EVENT_TYPE_CALL} from '@/utils/eventConstants';
import TranscriptionLogsTable from './TranscriptionLogsTable';
import AudioAnonymizationLogsTable from './AudioAnonymizationLogsTable';
import ProcessingLogsTable from './ProcessingLogsTable';
import ActionLogsTable from './ActionLogsTable';
import UserAccessLogsTable from './UserAccessLogsTable';
import Select from 'react-select';
import i18n from 'i18n';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface EventType {
    name: string;
}

interface EventDetails {
    id?: number;
    eventTypeName?: string;
    eventType?: EventType;
    eventCallId?: number;
    eventChatId?: number;
    eventEmailId?: number;
    eventFeedbackId?: number;
}

interface UserData {
    permissions?: string[];
}

interface AuditTabProps {
    eventDetails: EventDetails;
    userData: UserData;
    isAuditTabClicked: boolean;
}

interface AuditLog {
    key: string;
    label: string;
    condition: boolean;
    component: React.ReactNode;
}

interface SelectOption {
    value: string;
    label: string;
}

const AuditTab: React.FC<AuditTabProps> = ({eventDetails, userData, isAuditTabClicked}) => {
    const [selectedLog, setSelectedLog] = useState<string>('userAccessLog');
    const [shouldFetchLogs, setShouldFetchLogs] = useState<boolean>(false);

    useEffect(() => {
        if (selectedLog === 'userAccessLog' || isAuditTabClicked) {
            setShouldFetchLogs(true);
        }
    }, [selectedLog, isAuditTabClicked]);

    const resolveEventContentId = (eventDetails: EventDetails): number | null => {
        const eventTypeName = eventDetails?.eventTypeName || eventDetails?.eventType?.name;
        switch (eventTypeName) {
            case 'call':
                return eventDetails?.eventCallId || null;
            case 'chat':
                return eventDetails?.eventChatId || null;
            case 'email':
                return eventDetails?.eventEmailId || null;
            case 'feedback':
                return eventDetails?.eventFeedbackId || null;
            default:
                return null;
        }
    };

    const auditLogs: AuditLog[] = [
        {
            key: 'userAccessLog',
            label: i18n.t('audit_logs_user_access.text'),
            condition: hasPermission('fs.event.audit_trail.read', userData?.permissions, true) || false,
            component: isAuditTabClicked && eventDetails?.id ? (
                <UserAccessLogsTable eventId={eventDetails.id} shouldFetchLogs={shouldFetchLogs} />
            ) : null,
        },
        {
            key: 'transcriptionTasks',
            label: i18n.t('audit_transcription_tasks.text'),
            condition:
                eventDetails.eventType?.name === EVENT_TYPE_CALL &&
                (hasPermission('fs.event.call_transcription.read', userData?.permissions, true) || false),
            component: eventDetails?.eventCallId ? <TranscriptionLogsTable eventCallId={eventDetails.eventCallId} isAuditView={true} /> : null,
        },
        {
            key: 'anonymisationTasks',
            label: i18n.t('audit_audio_anonymisation.text'),
            condition:
                (eventDetails.eventTypeName === EVENT_TYPE_CALL || eventDetails.eventType?.name === EVENT_TYPE_CALL) &&
                (hasPermission('fs.audio_anonymisation.task.read', userData?.permissions, true) || false),
            component: eventDetails?.eventCallId ? <AudioAnonymizationLogsTable eventCallId={eventDetails.eventCallId} isAuditView={true} /> : null,
        },
        {
            key: 'processingLogs',
            label: i18n.t('audit_processing_logs.text'),
            condition: hasPermission('fs.event.processing_log.list', userData?.permissions, true) || false,
            component: (() => {
                const eventContentId = resolveEventContentId(eventDetails);
                const eventType = eventDetails?.eventTypeName || eventDetails?.eventType?.name;
                return eventContentId && eventType ? (
                    <ProcessingLogsTable
                        eventContentId={eventContentId}
                        eventType={eventType}
                    />
                ) : null;
            })(),
        },
        {
            key: 'actionLogs',
            label: i18n.t('audit_action_logs.text'),
            condition: hasPermission('fs.event.action_log.read', userData?.permissions, true) || false,
            component: (() => {
                const eventContentId = resolveEventContentId(eventDetails);
                const eventType = eventDetails?.eventTypeName || eventDetails?.eventType?.name;
                return eventContentId && eventType ? (
                    <ActionLogsTable
                        eventContentId={eventContentId}
                        eventType={eventType}
                    />
                ) : null;
            })(),
        },
    ];

    const selectedLogOption: SelectOption = {
        value: selectedLog,
        label: selectedLog
            ? auditLogs.find((log) => log.key === selectedLog)?.label || ''
            : i18n.t('audit_select_log.text'),
    };

    const selectOptions: SelectOption[] = auditLogs
        .filter((log) => log.condition)
        .map((log) => ({
            value: log.key,
            label: log.label,
        }));

    return (
        <Row>
            <Col className="mb-2 d-flex justify-content-start align-items-center justify-content-sm-end">
                <Form.Group className="mb-3" controlId="logSelection">
                    <Select<SelectOption>
                        value={selectedLogOption}
                        onChange={(selectedOption) => setSelectedLog(selectedOption?.value || '')}
                        options={selectOptions}
                        data-cy="audit-select-dropdown"
                    />
                </Form.Group>
            </Col>

            <Col md={12}>
                <Card className="pt-1 custom-border">
                    {auditLogs.find((log) => log.key === selectedLog)?.component}
                </Card>
            </Col>
        </Row>
    );
};

export default AuditTab; 